import { useEffect, useState } from 'react';
import Hero from '../components/home/Hero';
import SocialProofBar from '../components/home/SocialProofBar';
import ProblemSolution from '../components/home/ProblemSolution';
import AssessmentShowcase from '../components/home/AssessmentShowcase';
import Features from '../components/home/Features';
import PricingSection from '../components/home/PricingSection';
import TestimonialsSection from '../components/home/TestimonialsSection';
import FAQSection from '../components/home/FAQSection';
import CTASection from '../components/home/CTASection';
import NewsPreview from '../components/home/NewsPreview';
import { useAuth } from '../context/AuthContext';
import { useClinicProfile } from '../context/ClinicProfileContext';
import { defaultHomePageContent, mergeHomePageContent } from '../content/defaultTenantSitePages';
import { TenantSitePagesService } from '../services/TenantSitePagesService';
import type { TenantHomePageContent } from '../types/site-pages';
import { useTenantSlug } from '../context/TenantSlugContext';

const HomePage = () => {
  const { selectedClient } = useAuth();
  const { resolvedTenant } = useTenantSlug();
  const { effectiveTenantSlug } = useClinicProfile();
  const tenantName = selectedClient?.clientName || resolvedTenant?.clientName;
  const isTenantContext = Boolean(selectedClient || resolvedTenant);
  const [tenantContent, setTenantContent] = useState<TenantHomePageContent>(defaultHomePageContent);

  useEffect(() => {
    document.title = isTenantContext
      ? `${tenantName || 'Clínica'} - Site Institucional`
      : 'Revahub';
  }, [isTenantContext, tenantName]);

  useEffect(() => {
    let isCancelled = false;

    const loadTenantContent = async () => {
      if (!isTenantContext) {
        setTenantContent(defaultHomePageContent);
        return;
      }

      try {
        const pageRecord = selectedClient?.clientId
          ? await TenantSitePagesService.getByClientId(selectedClient.clientId, 'home')
          : effectiveTenantSlug
            ? await TenantSitePagesService.getPublicBySlug(effectiveTenantSlug, 'home')
            : null;

        if (isCancelled) {
          return;
        }

        setTenantContent(mergeHomePageContent(pageRecord?.contentJson ?? null));
      } catch (error) {
        console.error('Erro ao carregar conteúdo da home do tenant:', error);

        if (!isCancelled) {
          setTenantContent(defaultHomePageContent);
        }
      }
    };

    void loadTenantContent();

    return () => {
      isCancelled = true;
    };
  }, [effectiveTenantSlug, isTenantContext, resolvedTenant?.clientId, selectedClient?.clientId]);

  /* ─── Modo Tenant: site institucional da clínica ─── */
  if (isTenantContext) {
    return (
      <div>
        <Hero mode="tenant" tenantContent={tenantContent} />
        <Features mode="tenant" tenantContent={tenantContent} />
        <TestimonialsSection
          mode="tenant"
          tenantContent={tenantContent}
          tenantClientId={selectedClient?.clientId || resolvedTenant?.clientId || null}
        />
        <NewsPreview tenantContent={tenantContent} />
        <CTASection mode="tenant" tenantContent={tenantContent} />
      </div>
    );
  }

  /* ─── Modo Global: Landing page de venda de licenças RevaHub ─── */
  return (
    <div>
      {/* 1. Hero — Atenção: proposta de valor focada em avaliações + CTA principal */}
      <Hero mode="global" />

      {/* 2. Prova Social — Confiança imediata: logos + selos LGPD/COFFITO */}
      <SocialProofBar />

      {/* 3. Problema & Solução — Interesse: dores do gestor → benefícios */}
      <ProblemSolution />

      {/* 4. ★ DIFERENCIAL: Avaliações Clínicas — Showcase completo do sistema de laudos */}
      <AssessmentShowcase />

      {/* 5. Funcionalidades — Desejo: detalhamento dos módulos integrados */}
      <Features mode="global" />

      {/* 6. Depoimentos — Validação social */}
      <TestimonialsSection mode="global" />

      {/* 7. Preços — Decisão: planos com efeito decoy */}
      <PricingSection />

      {/* 8. FAQ — Quebra de objeções */}
      <FAQSection />

      {/* 9. CTA Final — Ação: última chamada para conversão */}
      <CTASection mode="global" />
    </div>
  );
};

export default HomePage;
